import React, { useState, useRef, useCallback } from 'react';
import QRCodeRenderer from './QRCodeRenderer';
import compassLogo from './logo-compass.png';
import './App.css';

// ── Tabs & shape options ───────────────────────────────────────────────

const INPUT_TABS = [
  { id: 'url', label: 'URL' },
  { id: 'text', label: 'Text' },
  { id: 'contact', label: 'Contact' },
];

const STYLE_TABS = [
  { id: 'colors', label: 'Colors' },
  { id: 'logo', label: 'Logo' },
  { id: 'shapes', label: 'Shapes' },
  { id: 'frame', label: 'Frame' },
];

const FRAME_OPTIONS = [
  { id: 'none', label: 'None' },
  { id: 'simple', label: 'Frame' },
  { id: 'scanme', label: 'Scan Me' },
  { id: 'arrow', label: 'Arrow' },
];

const BODY_SHAPES = [
  { id: 'square', label: 'Square' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'dots', label: 'Dots' },
  { id: 'diamond', label: 'Diamond' },
  { id: 'clover', label: 'Clover' },
];

const EYE_FRAME_SHAPES = [
  { id: 'square', label: 'Square' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'circle', label: 'Circle' },
  { id: 'leaf', label: 'Leaf' },
];

const EYE_BALL_SHAPES = [
  { id: 'square', label: 'Square' },
  { id: 'rounded', label: 'Rounded' },
  { id: 'circle', label: 'Circle' },
  { id: 'diamond', label: 'Diamond' },
];

// ── Helpers ────────────────────────────────────────────────────────────

function ensureUrl(input) {
  const trimmed = input.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return 'https://' + trimmed;
}

function buildVCard({ firstName, lastName, phone, email, org, url }) {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${lastName || ''};${firstName || ''};;;`,
    `FN:${[firstName, lastName].filter(Boolean).join(' ')}`,
  ];
  if (org) lines.push(`ORG:${org}`);
  if (phone) lines.push(`TEL:${phone}`);
  if (email) lines.push(`EMAIL:${email}`);
  if (url) lines.push(`URL:${ensureUrl(url)}`);
  lines.push('END:VCARD');
  return lines.join('\n');
}

// ── Tiny shape preview SVGs ────────────────────────────────────────────

function BodyPreview({ shape, active }) {
  const s = 6, g = 2, cols = 3;
  const w = cols * (s + g) - g;
  const rects = [];
  for (let r = 0; r < cols; r++)
    for (let c = 0; c < cols; c++) {
      if (r === 1 && c === 1 && shape !== 'dots') continue; // gap for visual
      const x = c * (s + g), y = r * (s + g);
      const cx2 = x + s / 2, cy2 = y + s / 2;
      let path;
      if (shape === 'square') path = <rect key={`${r}${c}`} x={x} y={y} width={s} height={s} />;
      else if (shape === 'rounded') path = <rect key={`${r}${c}`} x={x} y={y} width={s} height={s} rx={2} />;
      else if (shape === 'dots') path = <circle key={`${r}${c}`} cx={cx2} cy={cy2} r={s * 0.42} />;
      else if (shape === 'diamond') path = <polygon key={`${r}${c}`} points={`${cx2},${y} ${x + s},${cy2} ${cx2},${y + s} ${x},${cy2}`} />;
      else if (shape === 'clover') {
        const cr = s * 0.22, off = s * 0.14;
        path = <g key={`${r}${c}`}>
          <circle cx={cx2} cy={cy2 - off} r={cr} />
          <circle cx={cx2 + off} cy={cy2} r={cr} />
          <circle cx={cx2} cy={cy2 + off} r={cr} />
          <circle cx={cx2 - off} cy={cy2} r={cr} />
        </g>;
      }
      rects.push(path);
    }
  return (
    <svg viewBox={`0 0 ${w} ${w}`} className={`shape-icon ${active ? 'active' : ''}`}>
      <g fill="currentColor">{rects}</g>
    </svg>
  );
}

function EyeFramePreview({ shape, active }) {
  const s = 28;
  let inner;
  if (shape === 'square') {
    inner = <><rect x={0} y={0} width={s} height={s} /><rect x={4} y={4} width={s - 8} height={s - 8} fill="var(--preview-bg)" /></>;
  } else if (shape === 'rounded') {
    inner = <><rect x={0} y={0} width={s} height={s} rx={6} /><rect x={4} y={4} width={s - 8} height={s - 8} rx={3} fill="var(--preview-bg)" /></>;
  } else if (shape === 'circle') {
    inner = <><circle cx={s / 2} cy={s / 2} r={s / 2} /><circle cx={s / 2} cy={s / 2} r={s / 2 - 4} fill="var(--preview-bg)" /></>;
  } else if (shape === 'leaf') {
    inner = <>
      <rect x={0} y={0} width={s} height={s} rx={0} ry={0} style={{ borderRadius: '0 0 10px 0' }} />
      <path d={`M8,0 h${s - 8} a0,0 0 0 1 0,0 v${s - 8} a8,8 0 0 1 -8,8 h-${s - 8} a0,0 0 0 1 0,0 v-${s - 8} a8,8 0 0 1 8,-8 z`} />
      <path d={`M8,4 h${s - 16} a0,0 0 0 1 0,0 v${s - 16} a4,4 0 0 1 -4,4 h-${s - 16} a0,0 0 0 1 0,0 v-${s - 16} a4,4 0 0 1 4,-4 z`} fill="var(--preview-bg)" />
    </>;
  }
  return (
    <svg viewBox={`0 0 ${s} ${s}`} className={`shape-icon eye-icon ${active ? 'active' : ''}`}>
      <g fill="currentColor">{inner}</g>
    </svg>
  );
}

function EyeBallPreview({ shape, active }) {
  const s = 28;
  let inner;
  if (shape === 'square') inner = <rect x={8} y={8} width={12} height={12} />;
  else if (shape === 'rounded') inner = <rect x={8} y={8} width={12} height={12} rx={3} />;
  else if (shape === 'circle') inner = <circle cx={s / 2} cy={s / 2} r={6} />;
  else if (shape === 'diamond') inner = <polygon points={`${s / 2},6 ${s - 6},${s / 2} ${s / 2},${s - 6} 6,${s / 2}`} />;
  return (
    <svg viewBox={`0 0 ${s} ${s}`} className={`shape-icon eye-icon ${active ? 'active' : ''}`}>
      <g fill="currentColor">{inner}</g>
    </svg>
  );
}

// ── Frame preview icons ────────────────────────────────────────────────

function FramePreview({ type, active }) {
  const s = 32;
  const qr = 16; // inner QR square size
  const qx = (s - qr) / 2;
  const qy = type === 'scanme' ? (s - qr - 5) / 2 : (s - qr) / 2;
  const qrBlock = (
    <rect x={qx + 2} y={qy + 2} width={qr - 4} height={qr - 4} rx={1} fill="currentColor" opacity={0.3} />
  );

  let content;
  if (type === 'none') {
    content = (
      <>
        <rect x={qx} y={qy} width={qr} height={qr} rx={1} fill="none" stroke="currentColor" strokeWidth={0.8} strokeDasharray="2 1.5" />
        {qrBlock}
      </>
    );
  } else if (type === 'simple') {
    content = (
      <>
        <rect x={2} y={2} width={s - 4} height={s - 4} rx={3} fill="none" stroke="currentColor" strokeWidth={1.5} />
        {qrBlock}
      </>
    );
  } else if (type === 'scanme') {
    const bannerH = 6;
    const bannerY = s - 2 - bannerH;
    content = (
      <>
        <rect x={2} y={2} width={s - 4} height={s - 4} rx={3} fill="none" stroke="currentColor" strokeWidth={1.5} />
        <rect x={2} y={bannerY} width={s - 4} height={bannerH + 1} rx={2} fill="currentColor" />
        <text x={s / 2} y={bannerY + bannerH / 2 + 0.5} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize={3.2} fontWeight="800" fontFamily="Arial">SCAN ME</text>
        {qrBlock}
      </>
    );
  } else if (type === 'arrow') {
    content = (
      <>
        {qrBlock}
        <path d="M22,8 C26,9 26,14 23,15.5" fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" />
        <polygon points="22,15 23.8,14.2 23.2,16.2" fill="currentColor" />
        <text x={26} y={22} textAnchor="middle" dominantBaseline="middle" fill="currentColor" fontSize={3.5} fontWeight="800" fontFamily="Arial">SCAN</text>
        <text x={26} y={26} textAnchor="middle" dominantBaseline="middle" fill="currentColor" fontSize={3.5} fontWeight="800" fontFamily="Arial">ME</text>
      </>
    );
  }

  return (
    <svg viewBox={`0 0 ${s} ${s}`} className={`shape-icon frame-icon ${active ? 'active' : ''}`}>
      {content}
    </svg>
  );
}

// ── Main App ───────────────────────────────────────────────────────────

function App() {
  // Content state
  const [activeInput, setActiveInput] = useState('url');
  const [urlInput, setUrlInput] = useState('');
  const [textInput, setTextInput] = useState('');
  const [contact, setContact] = useState({
    firstName: '', lastName: '', phone: '', email: '', org: '', url: '',
  });

  // Style state
  const [activeStyle, setActiveStyle] = useState('colors');

  // Colors
  const [colorMode, setColorMode] = useState('single'); // single | gradient
  const [fgColor, setFgColor] = useState('#1a1a2e');
  const [gradColor1, setGradColor1] = useState('#1a1a2e');
  const [gradColor2, setGradColor2] = useState('#00914C');
  const [gradientType, setGradientType] = useState('linear');
  const [gradientAngle, setGradientAngle] = useState(135);

  // Shapes
  const [bodyShape, setBodyShape] = useState('square');
  const [eyeFrameShape, setEyeFrameShape] = useState('square');
  const [eyeBallShape, setEyeBallShape] = useState('square');

  // Frame
  const [frameType, setFrameType] = useState('none');
  const [frameColor, setFrameColor] = useState('#1a1a2e');

  // Logo
  const [logoSrc, setLogoSrc] = useState(null);
  const [logoFileName, setLogoFileName] = useState('');
  const [logoSize, setLogoSize] = useState(0.22);
  const [logoRemoveBg, setLogoRemoveBg] = useState(true);
  const [logoPadding, setLogoPadding] = useState(6);

  const qrRef = useRef(null);
  const fileInputRef = useRef(null);

  const getQRValue = () => {
    switch (activeInput) {
      case 'url': return ensureUrl(urlInput);
      case 'text': return textInput.trim();
      case 'contact': return buildVCard(contact);
      default: return '';
    }
  };

  const qrValue = getQRValue();

  const handleContactChange = (field) => (e) => {
    setContact((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleLogoUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoSrc(ev.target.result);
    reader.readAsDataURL(file);
  }, []);

  const removeLogo = () => {
    setLogoSrc(null);
    setLogoFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const vbW = parseFloat(svg.getAttribute('viewBox')?.split(' ')[2]) || 280;
    const vbH = parseFloat(svg.getAttribute('viewBox')?.split(' ')[3]) || 280;
    const scale = 1024 / Math.max(vbW, vbH);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(vbW * scale);
    canvas.height = Math.round(vbH * scale);
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const link = document.createElement('a');
      link.download = 'qrcode.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <div className="app">
      <header className="header">
        <img src={compassLogo} alt="Compass Hospitality" className="header-logo" />
        <h1>QR Code Generator</h1>
        <p className="subtitle">Generate & customize QR codes for URLs, text, or contacts</p>
      </header>

      <div className="layout">
        {/* ── Left column: inputs + styling ── */}
        <div className="controls-column">

          {/* Content input panel */}
          <div className="panel">
            <div className="panel-label">Content</div>
            <div className="tabs">
              {INPUT_TABS.map((t) => (
                <button key={t.id} className={`tab ${activeInput === t.id ? 'active' : ''}`} onClick={() => setActiveInput(t.id)}>{t.label}</button>
              ))}
            </div>
            <div className="panel-body">
              {activeInput === 'url' && (
                <div className="field">
                  <label htmlFor="url-input">Website URL</label>
                  <input id="url-input" type="text" placeholder="example.com" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} />
                  <span className="hint">https:// added automatically if omitted</span>
                </div>
              )}
              {activeInput === 'text' && (
                <div className="field">
                  <label htmlFor="text-input">Text Content</label>
                  <textarea id="text-input" placeholder="Enter any text..." rows={4} value={textInput} onChange={(e) => setTextInput(e.target.value)} />
                </div>
              )}
              {activeInput === 'contact' && (
                <>
                  <div className="field-row">
                    <div className="field"><label>First Name</label><input placeholder="John" value={contact.firstName} onChange={handleContactChange('firstName')} /></div>
                    <div className="field"><label>Last Name</label><input placeholder="Doe" value={contact.lastName} onChange={handleContactChange('lastName')} /></div>
                  </div>
                  <div className="field"><label>Phone</label><input type="tel" placeholder="+1 (555) 123-4567" value={contact.phone} onChange={handleContactChange('phone')} /></div>
                  <div className="field"><label>Email</label><input type="email" placeholder="john@example.com" value={contact.email} onChange={handleContactChange('email')} /></div>
                  <div className="field"><label>Organization</label><input placeholder="Acme Inc." value={contact.org} onChange={handleContactChange('org')} /></div>
                  <div className="field"><label>Website</label><input placeholder="example.com" value={contact.url} onChange={handleContactChange('url')} /></div>
                </>
              )}
            </div>
          </div>

          {/* Style panel */}
          <div className="panel">
            <div className="panel-label">Customize</div>
            <div className="tabs">
              {STYLE_TABS.map((t) => (
                <button key={t.id} className={`tab ${activeStyle === t.id ? 'active' : ''}`} onClick={() => setActiveStyle(t.id)}>{t.label}</button>
              ))}
            </div>
            <div className="panel-body">

              {/* ── Colors ── */}
              {activeStyle === 'colors' && (
                <>
                  <div className="toggle-row">
                    <button className={`toggle-btn ${colorMode === 'single' ? 'active' : ''}`} onClick={() => setColorMode('single')}>Single Color</button>
                    <button className={`toggle-btn ${colorMode === 'gradient' ? 'active' : ''}`} onClick={() => setColorMode('gradient')}>Gradient</button>
                  </div>

                  {colorMode === 'single' && (
                    <div className="color-row">
                      <label>Foreground Color</label>
                      <div className="color-input-wrap">
                        <input type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)} />
                        <input type="text" className="color-text" value={fgColor} onChange={(e) => setFgColor(e.target.value)} />
                      </div>
                    </div>
                  )}

                  {colorMode === 'gradient' && (
                    <>
                      <div className="color-row">
                        <label>Color 1</label>
                        <div className="color-input-wrap">
                          <input type="color" value={gradColor1} onChange={(e) => setGradColor1(e.target.value)} />
                          <input type="text" className="color-text" value={gradColor1} onChange={(e) => setGradColor1(e.target.value)} />
                        </div>
                      </div>
                      <div className="color-row">
                        <label>Color 2</label>
                        <div className="color-input-wrap">
                          <input type="color" value={gradColor2} onChange={(e) => setGradColor2(e.target.value)} />
                          <input type="text" className="color-text" value={gradColor2} onChange={(e) => setGradColor2(e.target.value)} />
                        </div>
                      </div>
                      <div className="toggle-row" style={{ marginTop: 12 }}>
                        <button className={`toggle-btn ${gradientType === 'linear' ? 'active' : ''}`} onClick={() => setGradientType('linear')}>Linear</button>
                        <button className={`toggle-btn ${gradientType === 'radial' ? 'active' : ''}`} onClick={() => setGradientType('radial')}>Radial</button>
                      </div>
                      {gradientType === 'linear' && (
                        <div className="slider-row">
                          <label>Angle: {gradientAngle}°</label>
                          <input type="range" min={0} max={360} value={gradientAngle} onChange={(e) => setGradientAngle(Number(e.target.value))} />
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Logo ── */}
              {activeStyle === 'logo' && (
                <>
                  <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
                    {logoSrc ? (
                      <img src={logoSrc} alt="Logo preview" className="logo-preview-img" />
                    ) : (
                      <div className="upload-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                        <span>Click to upload logo</span>
                        <span className="hint">PNG, JPG, or SVG</span>
                      </div>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoUpload} hidden />
                  </div>

                  {logoSrc && (
                    <>
                      <div className="logo-file-row">
                        <span className="logo-filename">{logoFileName}</span>
                        <button className="remove-btn" onClick={removeLogo}>Remove</button>
                      </div>
                      <div className="slider-row">
                        <label>Logo Size: {Math.round(logoSize * 100)}%</label>
                        <input type="range" min={10} max={35} value={Math.round(logoSize * 100)} onChange={(e) => setLogoSize(Number(e.target.value) / 100)} />
                      </div>
                      <div className="slider-row">
                        <label>Padding: {logoPadding}px</label>
                        <input type="range" min={0} max={20} value={logoPadding} onChange={(e) => setLogoPadding(Number(e.target.value))} />
                      </div>
                      <label className="checkbox-row">
                        <input type="checkbox" checked={logoRemoveBg} onChange={(e) => setLogoRemoveBg(e.target.checked)} />
                        <span>Remove background behind logo</span>
                      </label>
                    </>
                  )}
                </>
              )}

              {/* ── Shapes ── */}
              {activeStyle === 'shapes' && (
                <>
                  <div className="shape-section">
                    <label className="shape-section-label">Body Shape</label>
                    <div className="shape-grid">
                      {BODY_SHAPES.map((s) => (
                        <button key={s.id} className={`shape-btn ${bodyShape === s.id ? 'active' : ''}`} onClick={() => setBodyShape(s.id)} title={s.label}>
                          <BodyPreview shape={s.id} active={bodyShape === s.id} />
                          <span>{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="shape-section">
                    <label className="shape-section-label">Eye Frame Shape</label>
                    <div className="shape-grid">
                      {EYE_FRAME_SHAPES.map((s) => (
                        <button key={s.id} className={`shape-btn ${eyeFrameShape === s.id ? 'active' : ''}`} onClick={() => setEyeFrameShape(s.id)} title={s.label}>
                          <EyeFramePreview shape={s.id} active={eyeFrameShape === s.id} />
                          <span>{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="shape-section">
                    <label className="shape-section-label">Eye Ball Shape</label>
                    <div className="shape-grid">
                      {EYE_BALL_SHAPES.map((s) => (
                        <button key={s.id} className={`shape-btn ${eyeBallShape === s.id ? 'active' : ''}`} onClick={() => setEyeBallShape(s.id)} title={s.label}>
                          <EyeBallPreview shape={s.id} active={eyeBallShape === s.id} />
                          <span>{s.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ── Frame ── */}
              {activeStyle === 'frame' && (
                <>
                  <div className="shape-section">
                    <label className="shape-section-label">Frame Style</label>
                    <div className="shape-grid">
                      {FRAME_OPTIONS.map((f) => (
                        <button key={f.id} className={`shape-btn ${frameType === f.id ? 'active' : ''}`} onClick={() => setFrameType(f.id)} title={f.label}>
                          <FramePreview type={f.id} active={frameType === f.id} />
                          <span>{f.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  {frameType !== 'none' && (
                    <div className="color-row" style={{ marginTop: 8 }}>
                      <label>Frame Color</label>
                      <div className="color-input-wrap">
                        <input type="color" value={frameColor} onChange={(e) => setFrameColor(e.target.value)} />
                        <input type="text" className="color-text" value={frameColor} onChange={(e) => setFrameColor(e.target.value)} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── Right column: QR preview ── */}
        <div className="preview-column">
          <div className="preview-sticky">
            <div className="qr-wrapper" ref={qrRef}>
              {qrValue ? (
                <QRCodeRenderer
                  value={qrValue}
                  size={280}
                  fgColor={fgColor}
                  gradientEnabled={colorMode === 'gradient'}
                  gradientColor1={gradColor1}
                  gradientColor2={gradColor2}
                  gradientType={gradientType}
                  gradientAngle={gradientAngle}
                  bodyShape={bodyShape}
                  eyeFrameShape={eyeFrameShape}
                  eyeBallShape={eyeBallShape}
                  logoSrc={logoSrc}
                  logoSize={logoSize}
                  logoRemoveBg={logoRemoveBg}
                  logoPadding={logoPadding}
                  frameType={frameType}
                  frameColor={frameColor}
                />
              ) : (
                <div className="qr-placeholder">
                  <span>Enter content to generate a QR code</span>
                </div>
              )}
            </div>

            {qrValue && (
              <button className="download-btn" onClick={downloadQR}>Download PNG</button>
            )}

            {activeInput === 'url' && qrValue && (
              <p className="encoded-url">Encodes: <code>{qrValue}</code></p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
